import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import Sidebar from '../components/Sidebar';
import Modal from '../components/Modal';
import { useCurrentInternalUser } from '../lib/currentUser';
import { fmtDate } from '../lib/dates';
import { normaliseProjectRef, DEBRIEF_STATUS_LABEL, actionsWithContext } from '../lib/projectDebriefs';
import DebriefActionsList from '../components/debrief/DebriefActionsList';
import { startOrOpenDebrief } from '../lib/debriefCreate';

// ============================================================================
// Project debriefs — the list, and starting a new one
// ============================================================================
// A debrief belongs to a BRIEF (Rob's call, 2026-08-14). It used to belong to a
// PROJECT, keyed on the FS number, so that a job with four suppliers produced
// one write-up rather than four. That collided in practice: two briefs carrying
// the same number for different clients shared a debrief, and opening it from
// one brief landed on the other one's.
//
// The FS number, project name and client are still copied onto the debrief so it
// reads correctly years after a brief is renamed or archived — and resurfacing
// still matches past actions on CLIENT, which stays a project-level question.
// You pick a brief rather than typing a number, so a typo can't fork anything.
// ============================================================================

const FONT = "font-['Metropolis']";

export default function Debriefs() {
  const navigate = useNavigate();
  const { user: currentUser } = useCurrentInternalUser();

  const [debriefs, setDebriefs] = useState([]);
  const [openCounts, setOpenCounts] = useState({}); // debrief_id → open actions
  const [projects, setProjects] = useState([]);     // distinct FS numbers off briefs
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [chosenRef, setChosenRef] = useState('');
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  // §4 — actions across every debrief. They're the part of a debrief that
  // outlives it, so they get a view of their own rather than being buried one
  // project at a time.
  const [tab, setTab] = useState('debriefs'); // 'debriefs' | 'actions'
  const [allActions, setAllActions] = useState([]);
  const [actionFilters, setActionFilters] = useState({ owner: '', status: 'open', topic: '', search: '' });
  const [busyAction, setBusyAction] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    // Topics and notes come along so an action can name what it's about and
    // which topic it came from — an action list that only shows the owner and a
    // status is a to-do list with the reason stripped out.
    const [{ data: rows, error: e }, { data: actionRows }, { data: briefRows }, { data: topicRows }, { data: noteRows }] = await Promise.all([
      supabase.from('project_debriefs')
        .select('*, written_by_user:internal_users!written_by(full_name)')
        .order('debrief_date', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false }),
      supabase.from('debrief_actions').select('*'),
      supabase.from('briefs')
        .select('id, brief_name, fusion_project_number, fusion_project_name, client, event_live_date')
        .not('fusion_project_number', 'is', null),
      supabase.from('debrief_topics').select('id, debrief_id, title'),
      supabase.from('debrief_notes').select('id, topic_id, body'),
    ]);
    if (e) { setError(e.message); setLoading(false); return; }
    setDebriefs(rows || []);

    const counts = {};
    for (const a of actionRows || []) {
      if (a.status !== 'open') continue;
      counts[a.debrief_id] = (counts[a.debrief_id] || 0) + 1;
    }
    setOpenCounts(counts);
    setAllActions(actionsWithContext({
      debriefs: rows || [], topics: topicRows || [], notes: noteRows || [], actions: actionRows || [],
    }));

    // A debrief belongs to a BRIEF (2026-08-14), so the picker offers briefs
    // rather than projects. It used to collapse briefs down to distinct FS
    // numbers, which is exactly what made two briefs sharing a number share a
    // debrief. Newest event first.
    setProjects(
      (briefRows || [])
        .filter(b => normaliseProjectRef(b.fusion_project_number))
        .map(b => ({
          briefId: b.id,
          ref: normaliseProjectRef(b.fusion_project_number),
          briefName: b.brief_name,
          name: b.fusion_project_name,
          client: b.client,
          event_live_date: b.event_live_date,
        }))
        .sort((a, b) => String(b.event_live_date || '').localeCompare(String(a.event_live_date || ''))),
    );
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Briefs that already have a debrief are shown as such rather than hidden —
  // silently dropping the option would look like a bug, and picking one just
  // opens the existing debrief rather than failing.
  const debriefedBriefIds = useMemo(
    () => new Set((debriefs || []).map(d => d.brief_id).filter(Boolean)),
    [debriefs],
  );

  // Brief name per debrief. A row titled only by project would be identical for
  // two debriefs on the same FS number, which is exactly the case that now
  // exists — the brief name is the only thing separating them.
  const briefNameById = useMemo(
    () => new Map(projects.map(p => [p.briefId, p.briefName])),
    [projects],
  );
  const titleOf = useCallback(
    (d) => briefNameById.get(d.brief_id)
      || [d.project_ref, d.project_name].filter(Boolean).join(' · ')
      || 'Debrief',
    [briefNameById],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return debriefs;
    return debriefs.filter(d =>
      [d.project_ref, d.project_name, d.client, briefNameById.get(d.brief_id)]
        .filter(Boolean).some(v => String(v).toLowerCase().includes(q)));
  }, [debriefs, search, briefNameById]);

  // Shared with the "start the debrief" prompt on a brief's feedback card, so
  // the two can't create differently shaped records (lib/debriefCreate).
  const createDebrief = async () => {
    const project = projects.find(p => p.briefId === chosenRef);
    if (!project || creating) return;
    setCreating(true);
    const res = await startOrOpenDebrief({
      briefId: project.briefId,
      projectRef: project.ref,
      projectName: project.name,
      client: project.client,
      writtenBy: currentUser?.id || null,
    });
    setCreating(false);
    if (res.error) { setError(res.error); return; }
    navigate(`/debriefs/${res.id}`);
  };

  return (
    <div className="min-h-screen bg-canvas flex">
      <Sidebar currentInternalUser={currentUser} />
      <div className="flex-1 flex flex-col min-w-0">
        <div className="px-8 pt-8 pb-16 max-w-[1100px]">
          <div className="flex items-end justify-between gap-4 flex-wrap mb-2">
            <div>
              <h1 className={`${FONT} text-[26px] font-extrabold tracking-tight text-ink-primary`}>Project debriefs</h1>
              <p className={`${FONT} text-[13px] text-fusion-mid-grey mt-1 max-w-2xl leading-relaxed`}>
                What happened on a project, what we learned, and what someone now has to do about it.
                Internal — a debrief is never visible to suppliers.
              </p>
            </div>
            <button type="button" onClick={() => { setChosenRef(''); setShowNew(true); }}
              className={`${FONT} text-[13px] font-bold rounded-full px-5 py-2.5 text-white bg-[#2F6E14] hover:brightness-110`}>
              New debrief
            </button>
          </div>

          {error && <p className={`${FONT} text-[13px] text-[#E0290A] mb-3`}>{error}</p>}

          {/* Two views of the same records: the debriefs themselves, and every
              action they raised. */}
          <div className="flex items-center gap-1 border-b border-[#E6EADB] mb-5" role="tablist">
            {[
              { key: 'debriefs', label: 'Debriefs', count: debriefs.length },
              { key: 'actions', label: 'Actions', count: allActions.filter(a => a.status === 'open').length },
            ].map(t => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                onClick={() => setTab(t.key)}
                className={`${FONT} text-[13px] font-semibold px-3.5 py-2 -mb-px border-b-2 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest ${
                  tab === t.key ? 'border-[#92D400] text-ink' : 'border-transparent text-[#51534A] hover:text-ink'
                }`}>
                {t.label}
                {t.count > 0 && (
                  <span className={`ml-2 text-[11px] font-bold rounded-full px-2 py-0.5 ${tab === t.key ? 'bg-[#92D400]/20 text-fusion-forest' : 'bg-[#EEF1E6] text-[#51534A]'}`}>{t.count}</span>
                )}
              </button>
            ))}
          </div>

          {tab === 'actions' ? (
            <DebriefActionsList
              actions={allActions}
              filters={actionFilters}
              onFilters={setActionFilters}
              busyId={busyAction}
              onOpenDebrief={(id) => navigate(`/debriefs/${id}`)}
              onSetStatus={async (action, status) => {
                setBusyAction(action.id);
                const { error: e2 } = await supabase.from('debrief_actions')
                  .update({
                    status,
                    closed_at: status === 'open' ? null : new Date().toISOString(),
                    closed_by: status === 'open' ? null : (currentUser?.id || null),
                  })
                  .eq('id', action.id);
                setBusyAction(null);
                if (e2) { setError(e2.message); return; }
                // Re-derive rather than patching in place, so the tab counts and
                // the per-debrief open counts can't drift from the rows.
                load();
              }}
            />
          ) : (<>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by project number, name or client"
            className={`${FONT} w-full max-w-md bg-white border border-line rounded-lg px-3 py-2 text-sm mb-5 focus:outline-none focus:border-[#92D400]`} />

          {loading ? (
            <p className={`${FONT} text-sm text-fusion-mid-grey`}>Loading…</p>
          ) : visible.length === 0 ? (
            <div className="bg-white border border-[#E6EADB] rounded-2xl p-6">
              <p className={`${FONT} text-[13px] text-fusion-mid-grey`}>
                {debriefs.length === 0
                  ? 'No debriefs yet. Start one after a project finishes, while everyone still remembers it.'
                  : 'Nothing matches that search.'}
              </p>
            </div>
          ) : (
            <div className="bg-white border border-[#E6EADB] rounded-2xl overflow-hidden">
              {visible.map(d => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => navigate(`/debriefs/${d.id}`)}
                  className="w-full text-left flex items-start gap-4 px-5 py-3.5 border-b border-[#F0F2E8] last:border-0 hover:bg-[#F7F9F0]/60 transition-colors">
                  <div className="min-w-0 flex-1">
                    <div className={`${FONT} text-[13px] font-bold text-ink`}>
                      {titleOf(d)}
                    </div>
                    <div className={`${FONT} text-[11px] text-[#51534A] mt-0.5`}>
                      {[d.project_ref, d.project_name, d.client,
                        d.debrief_date ? fmtDate(d.debrief_date) : null, d.written_by_user?.full_name]
                        .filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {openCounts[d.id] > 0 && (
                      <span className={`${FONT} text-[10px] font-bold uppercase tracking-[0.12em] rounded-full px-2.5 py-1 text-[#7A4E10] bg-[#E89A2E]/12 border border-[#E89A2E]/40`}>
                        {openCounts[d.id]} open
                      </span>
                    )}
                    <span className={`${FONT} text-[10px] font-bold uppercase tracking-[0.12em] rounded-full px-2.5 py-1 border ${
                      d.status === 'published'
                        ? 'text-fusion-forest bg-[#92D400]/12 border-[#92D400]/40'
                        : 'text-[#51534A] bg-[#51534A]/8 border-[#DADCCD]'
                    }`}>
                      {DEBRIEF_STATUS_LABEL[d.status] || d.status}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
          </>)}
        </div>
      </div>

      <Modal open={showNew} onClose={() => setShowNew(false)} maxWidth={560} ariaLabel="New debrief">
        <Modal.Header eyebrow="New debrief" title="Which brief?" sub="Every brief gets its own debrief." onClose={() => setShowNew(false)} />
        <Modal.Body>
          {projects.length === 0 ? (
            <p className={`${FONT} text-[13px] text-fusion-mid-grey`}>
              No briefs carry an FS project number yet, so there's nothing to debrief.
            </p>
          ) : (
            <>
              <label htmlFor="debrief-project" className={`${FONT} block text-[11px] font-bold uppercase tracking-[0.14em] text-[#51534A] mb-1.5`}>Brief</label>
              <select id="debrief-project" value={chosenRef} onChange={e => setChosenRef(e.target.value)}
                className={`${FONT} w-full bg-white border border-line rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#92D400]`}>
                <option value="">Choose a brief…</option>
                {/* Brief name first — it's what distinguishes two briefs on one
                    FS number, which is the case that used to collide. */}
                {projects.map(p => (
                  <option key={p.briefId} value={p.briefId}>
                    {p.briefName || p.name || p.ref}{p.ref ? ` · ${p.ref}` : ''}{p.client ? ` · ${p.client}` : ''}
                    {debriefedBriefIds.has(p.briefId) ? ' (already debriefed)' : ''}
                  </option>
                ))}
              </select>
              <p className={`${FONT} text-[11px] text-[#7C7F6F] mt-2 leading-snug`}>
                You pick rather than type. Two briefs can share an FS number — they get a debrief each, so
                choosing the brief is what keeps them apart.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={() => setShowNew(false)}
              className={`${FONT} text-[13px] font-bold text-[#51534A] px-4 py-2.5 rounded-lg border border-line bg-white hover:border-[#DADCCD]`}>Cancel</button>
            <button type="button" onClick={createDebrief} disabled={!chosenRef || creating}
              className={`${FONT} text-[13px] font-bold text-white px-5 py-2.5 rounded-lg bg-[#2F6E14] hover:brightness-110 disabled:opacity-40`}>
              {creating ? 'Creating…' : 'Start debrief'}
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
